/**
 * Subtitle bar stylesheet — injected on first paint via `ensureStyles`.
 * Positioning lives in ../placement.ts; this file only owns chrome,
 * typography, scroll behaviour, indicator + choice + streaming visuals.
 */

import { injectScopedStyle, UI_ATTR } from '../../utils/dom';

export const STYLE_ID = 'dddk-subtitle-style';

export function ensureStyles(): void {
  injectScopedStyle(STYLE_ID, `
    /* Theming prefers host-bridged tokens (--dddk-bg-elevated / --dddk-text
     * / --dddk-border) so a host that defines a dark theme via those
     * tokens gets a properly-themed bar without overriding --dddk-bar-bg. */
    [${UI_ATTR}="bar"] {
      position: relative;
      background: var(--dddk-bar-bg, var(--dddk-bg-elevated, rgba(255, 255, 255, 0.985)));
      color: var(--dddk-bar-text, var(--dddk-text, #1a1a1a));
      border: 1px solid var(--dddk-bar-border, var(--dddk-border, rgba(0, 0, 0, 0.08)));
      border-radius: var(--dddk-bar-radius, 16px);
      box-shadow: var(--dddk-bar-shadow, 0 18px 48px -12px rgba(0, 0, 0, 0.32), 0 4px 14px rgba(0, 0, 0, 0.08));
      padding: 0;
      font-family: var(--dddk-bar-font, var(--dddk-font, system-ui, -apple-system, "Segoe UI", "PingFang TC", "Microsoft JhengHei", sans-serif));
      font-size: var(--dddk-bar-font-size, 15.5px);
      line-height: 1.65;
      letter-spacing: 0.005em;
      backdrop-filter: blur(var(--dddk-blur, 18px));
      -webkit-backdrop-filter: blur(var(--dddk-blur, 18px));
      /* No transform animation on the bar itself — placement.ts
         positions the bar with translateX(-50%) for centring, and
         animating transform overrode that (bar visibly landed off-
         centre to the right, then jumped back). Use a pseudo layer
         for entrance visuals if we want richer choreo. */
    }
    /* Content fade-in on the scroll container (safe — doesn't fight
       the outer transform). */
    [${UI_ATTR}="bar-scroll"] {
      animation: dddk-bar-scroll-fade 280ms ease-out;
    }
    @keyframes dddk-bar-scroll-fade {
      from { opacity: 0; transform: translateY(4px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @media (prefers-reduced-motion: reduce) {
      [${UI_ATTR}="bar-scroll"] { animation: none; }
    }
    [${UI_ATTR}="bar-scroll"] {
      max-height: var(--dddk-bar-max-height, min(52vh, 460px));
      overflow-y: auto;
      overscroll-behavior: contain;
      padding: 18px 56px 18px 22px;
      display: flex; flex-direction: column; gap: 10px;
      scrollbar-width: thin;
      scrollbar-color: var(--dddk-border, rgba(0,0,0,0.18)) transparent;
    }
    /* Mascot avatar — a duck face pinned at the LEFT edge of the bar
       (mirroring the × close on the right). When present, bar-scroll
       gets extra left padding so the text doesn't slide under it.
       Entrance: swims IN from the direction of the FAB (bottom-right)
       — reads as "the duck flew over from its perch to deliver this
       message". Then idle-bob loops so the avatar stays alive. */
    [${UI_ATTR}="bar-avatar"] {
      position: absolute;
      top: 50%; left: 14px;
      width: 36px; height: 36px;
      pointer-events: none;
      user-select: none;
      z-index: 2;
      /* First: swim-in from bottom-right; then: idle bob loop. Comma-
         separated animations run in parallel but bounded ones (swim-in)
         land on their final state via the "both" fill mode. */
      animation:
        dddk-bar-avatar-swim var(--dddk-avatar-swim-duration, 620ms) var(--dddk-avatar-swim-easing, cubic-bezier(0.34, 1.4, 0.64, 1)) both,
        dddk-bar-avatar-bob var(--dddk-avatar-bob-duration, 2.6s) ease-in-out infinite var(--dddk-avatar-swim-duration, 620ms);
      transform: translateY(-50%);
    }
    @keyframes dddk-bar-avatar-swim {
      0%   { transform: translate(80px, calc(-50% + 40px)) rotate(-20deg) scale(0.6); opacity: 0; }
      70%  { opacity: 1; }
      100% { transform: translate(0, -50%) rotate(0deg) scale(1); opacity: 1; }
    }
    @keyframes dddk-bar-avatar-bob {
      0%, 100% { transform: translateY(-50%); }
      50%      { transform: translateY(calc(-50% - 3px)); }
    }
    /* When the avatar is rendered, push subtitle content right so the
       face doesn't overlap the text. Uses :has() — supported on all
       modern engines that dddk targets. */
    [${UI_ATTR}="bar"]:has([${UI_ATTR}="bar-avatar"]) [${UI_ATTR}="bar-scroll"] {
      padding-left: 62px;
    }
    @media (prefers-reduced-motion: reduce) {
      [${UI_ATTR}="bar-avatar"] { animation: none; }
    }
    [${UI_ATTR}="bar-scroll"]::-webkit-scrollbar { width: 8px; }
    [${UI_ATTR}="bar-scroll"]::-webkit-scrollbar-track { background: transparent; }
    [${UI_ATTR}="bar-scroll"]::-webkit-scrollbar-thumb {
      background: var(--dddk-border, rgba(0,0,0,0.18));
      border-radius: 4px;
    }
    [${UI_ATTR}="bar-close"] {
      position: absolute;
      top: 10px; right: 10px;
      width: 32px; height: 32px;
      display: inline-flex; align-items: center; justify-content: center;
      background: var(--dddk-close-bg, rgba(0,0,0,0.05));
      color: var(--dddk-close-fg, var(--dddk-text, #1a1a1a));
      border: 1px solid var(--dddk-close-border, rgba(0,0,0,0.08));
      border-radius: 10px;
      font-size: 18px; line-height: 1;
      font-weight: 500;
      cursor: pointer;
      padding: 0;
      transition: background 0.12s, color 0.12s, border-color 0.12s, transform 0.08s;
      z-index: 2;
      -webkit-tap-highlight-color: transparent;
    }
    [${UI_ATTR}="bar-close"]:hover {
      background: var(--dddk-close-bg-hover, rgba(0,0,0,0.1));
      border-color: var(--dddk-close-border-hover, rgba(0,0,0,0.16));
    }
    [${UI_ATTR}="bar-close"]:focus-visible {
      outline: 2px solid var(--dddk-accent, #6366f1);
      outline-offset: 2px;
    }
    [${UI_ATTR}="bar-close"]:active { transform: scale(0.92); }
    [${UI_ATTR}="bar-hints"] {
      color: var(--dddk-bar-hints, var(--dddk-text-muted, #6b7280));
      font-size: var(--dddk-font-size-sm, 12.5px);
      margin-top: 2px;
    }
    [${UI_ATTR}="bar-text"] { line-height: 1.65; font-size: inherit; }
    /* Streaming mode: a blinking block cursor sits at the tail of the
       live Text node so users know more text is on the way. */
    [${UI_ATTR}="bar"][data-dddk-bar-mode="streaming"] [${UI_ATTR}="bar-text"] {
      white-space: pre-wrap;
      word-break: break-word;
    }
    [${UI_ATTR}="streaming-cursor"] {
      display: inline-block;
      margin-left: 2px;
      color: var(--dddk-accent, #6366f1);
      animation: dddk-cursor-blink 1s steps(2) infinite;
      font-weight: 400;
      transform: translateY(-1px);
    }
    [${UI_ATTR}="bar"][data-dddk-bar-mode="streaming-done"] [${UI_ATTR}="bar-text"] {
      white-space: pre-wrap;
      word-break: break-word;
    }
    @keyframes dddk-cursor-blink {
      0%, 49% { opacity: 1; }
      50%, 100% { opacity: 0; }
    }
    /* Pause hint attaches BELOW the streamed text without replacing the
       bar, so the user can finish reading before deciding. */
    [${UI_ATTR}="streaming-pause"] {
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px dashed var(--dddk-border, rgba(0, 0, 0, 0.12));
      display: flex; flex-direction: column; gap: 4px;
    }
    [${UI_ATTR}="streaming-pause-text"] {
      font-size: var(--dddk-font-size-md, 14.5px);
      color: var(--dddk-text, #1a1a1a);
    }
    [${UI_ATTR}="streaming-pause-hints"] {
      font-size: var(--dddk-font-size-sm, 12.5px);
      color: var(--dddk-text-muted, #6b7280);
    }
    [${UI_ATTR}="bar-text"] strong { font-weight: 700; }
    [${UI_ATTR}="bar-text"] em { font-style: italic; }
    [${UI_ATTR}="bar-text"] code {
      font-family: var(--dddk-mono, ui-monospace, "SF Mono", Menlo, monospace);
      font-size: 0.92em;
      padding: 1px 5px;
      background: var(--dddk-row-hover, rgba(0,0,0,0.06));
      border-radius: 4px;
    }
    [${UI_ATTR}="bar-text"] ul,
    [${UI_ATTR}="bar-text"] ol {
      margin: 6px 0 6px 0;
      padding-left: 20px;
    }
    [${UI_ATTR}="bar-text"] li { margin: 2px 0; }
    [${UI_ATTR}="bar-text"] a {
      color: var(--dddk-accent, #6366f1);
      text-decoration: underline;
    }
    [${UI_ATTR}="bar-hints"] {
      font-size: var(--dddk-font-size-sm, 12px);
    }
    [${UI_ATTR}="bar-buttons"] {
      display: flex; gap: 8px; justify-content: flex-end;
      margin-top: 4px;
    }
    /* Put hint text and action buttons on the SAME row so a proactive
       bar's "space 好 ｜ 雙擊 space 暫時不用 ｜ Esc 略過" hint sits
       next to the ✓/✕ buttons instead of taking its own row below.
       Uses grid: text stays on its own row above, hints+buttons share
       the row beneath. */
    [${UI_ATTR}="bar-scroll"]:has([${UI_ATTR}="bar-hints"]):has([${UI_ATTR}="bar-buttons"]) {
      display: grid;
      grid-template-columns: 1fr auto;
      gap: 8px 16px;
      align-items: center;
    }
    [${UI_ATTR}="bar-scroll"]:has([${UI_ATTR}="bar-hints"]):has([${UI_ATTR}="bar-buttons"]) [${UI_ATTR}="bar-text"] {
      grid-column: 1 / -1;
    }
    [${UI_ATTR}="bar-scroll"]:has([${UI_ATTR}="bar-hints"]):has([${UI_ATTR}="bar-buttons"]) [${UI_ATTR}="bar-hints"] {
      grid-column: 1;
      margin: 0;
    }
    [${UI_ATTR}="bar-scroll"]:has([${UI_ATTR}="bar-hints"]):has([${UI_ATTR}="bar-buttons"]) [${UI_ATTR}="bar-buttons"] {
      grid-column: 2;
      margin: 0;
    }
    [${UI_ATTR}="bar-buttons"] button {
      border: 0;
      /* 44x44 minimum tap target per WCAG. */
      min-width: 44px; min-height: 36px;
      padding: 6px 16px;
      border-radius: var(--dddk-radius-sm, 6px);
      background: var(--dddk-accent, #ec4899);
      color: var(--dddk-text-on-accent, #fff);
      cursor: pointer;
      font-size: var(--dddk-font-size-md, 14px);
      font-weight: 600;
      display: inline-flex; align-items: center; justify-content: center; gap: 4px;
      -webkit-tap-highlight-color: transparent;
      transition: background 0.1s, transform 0.08s;
    }
    [${UI_ATTR}="bar-buttons"] button:hover {
      background: var(--dddk-accent-hover, #db2777);
    }
    [${UI_ATTR}="bar-buttons"] button:active {
      transform: scale(0.96);
    }
    /* Reject reads as secondary so accept stays the primary action. */
    [${UI_ATTR}="bar-buttons"] button[data-dddk-action="reject"] {
      background: transparent;
      color: var(--dddk-text-muted, #6b6b6b);
      border: 1px solid var(--dddk-border, rgba(0,0,0,0.15));
    }
    [${UI_ATTR}="bar-buttons"] button[data-dddk-action="reject"]:hover {
      background: var(--dddk-row-hover, rgba(0,0,0,0.04));
      color: var(--dddk-text, #1a1a1a);
    }
    /* Multi-choice mode — question + N numbered rows + optional
       free-text input + hint line. */
    [${UI_ATTR}="choice-list"] {
      display: flex; flex-direction: column; gap: 4px;
      margin: 4px 0 2px;
    }
    [${UI_ATTR}="choice-row"] {
      display: flex; align-items: center; gap: 10px;
      width: 100%;
      padding: 7px 10px;
      border: 1px solid transparent;
      border-radius: var(--dddk-radius-sm, 6px);
      background: var(--dddk-row-bg, transparent);
      color: inherit;
      font: inherit;
      cursor: pointer;
      text-align: left;
    }
    [${UI_ATTR}="choice-row"]:hover,
    [${UI_ATTR}="choice-row"]:focus-visible {
      background: var(--dddk-row-hover, rgba(255,220,80,0.14));
      border-color: var(--dddk-border-subtle, rgba(0,0,0,0.08));
      outline: none;
      transform: translateX(2px);
      transition: transform 0.15s ease-out, background 0.15s ease-out;
    }
    /* Duck-key: the numeric picker kbd becomes a small yellow disc so
       the choice list feels like it belongs to the mascot. Circle,
       warm-yellow fill, dark-navy digit — same palette as the FAB
       face. Hosts can override via --dddk-choice-key-* variables. */
    [${UI_ATTR}="choice-key"] {
      display: inline-flex; align-items: center; justify-content: center;
      min-width: 22px; height: 22px;
      padding: 0 7px;
      border-radius: 50%;
      background: var(--dddk-choice-key-bg, #ffd93d);
      color: var(--dddk-choice-key-fg, #1a2a4a);
      border: 1px solid var(--dddk-choice-key-border, rgba(20,30,60,0.15));
      font-size: 11px;
      font-family: var(--dddk-mono, ui-monospace, monospace);
      font-weight: 700;
      box-shadow: 0 1px 2px rgba(0,0,0,0.06);
    }
    [${UI_ATTR}="choice-row"]:hover [${UI_ATTR}="choice-key"] {
      background: var(--dddk-choice-key-bg-hover, #ffcf1a);
    }
    [${UI_ATTR}="choice-label"] { font-size: var(--dddk-font-size-md, 14px); }
    [${UI_ATTR}="choice-freetext"] { margin-top: 4px; }
    [${UI_ATTR}="choice-input"] {
      width: 100%;
      padding: 7px 10px;
      border: 1px solid var(--dddk-border, rgba(0,0,0,0.12));
      border-radius: var(--dddk-radius-sm, 6px);
      background: var(--dddk-input-bg, transparent);
      color: inherit;
      font: inherit;
    }
    [${UI_ATTR}="choice-input"]:focus {
      outline: none;
      border-color: var(--dddk-accent, #ec4899);
    }
    /* Indicator background uses ACCENT (not bg-elevated) so it stays
       visible on dark themes; "listening" gets a pulsing ring so the
       hot mic state is obvious. */
    [${UI_ATTR}="indicator"] {
      background: var(--dddk-accent, #6366f1);
      color: var(--dddk-text-on-accent, #fff);
      border-radius: var(--dddk-radius-pill, 999px);
      padding: 10px 20px;
      box-shadow: var(--dddk-shadow-md, 0 6px 20px rgba(0,0,0,0.25));
      display: flex; align-items: center; gap: 10px;
      font-size: var(--dddk-font-size-md, 14px);
      font-weight: 600;
      border: 1px solid rgba(255,255,255,0.15);
    }
    @media (max-width: 480px) {
      [${UI_ATTR}="indicator"] {
        padding: 6px 14px;
        gap: 8px;
        font-size: var(--dddk-font-size-sm, 13px);
      }
      [${UI_ATTR}="indicator-dots"] span {
        width: 6px; height: 6px;
      }
      [${UI_ATTR}="indicator-check"] {
        font-size: 14px;
      }
    }
    [${UI_ATTR}="indicator"][data-state="listening"] {
      animation: dddk-indicator-pulse 1.4s ease-in-out infinite;
    }
    [${UI_ATTR}="indicator"][data-state="done"] {
      background: var(--dddk-success, #16a34a);
    }
    [${UI_ATTR}="indicator-check"] {
      font-size: 16px; line-height: 1;
    }
    [${UI_ATTR}="indicator-dots"] {
      display: inline-flex; gap: 5px;
      align-items: center;
    }
    /* Default dot appearance — solid pill. When the host sets
       --dddk-indicator-duck-url, the spans switch to duck-face pips:
       larger, transparent bg, image inside. Enables the "three little
       ducks bouncing" thinking indicator without JS changes. */
    [${UI_ATTR}="indicator-dots"] span {
      width: 7px; height: 7px; border-radius: 50%;
      background: var(--dddk-text-on-accent, #fff);
      animation: dddk-dot-bounce 1.2s infinite ease-in-out;
      background-image: var(--dddk-indicator-duck-url, none);
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center;
    }
    /* When the duck URL is set, size up the pips so faces are readable. */
    [${UI_ATTR}="indicator"]:where([style*="--dddk-indicator-duck-url"], :root) [${UI_ATTR}="indicator-dots"] span {
      /* This selector is intentionally lax — the actual size upgrade
         relies on the variable being defined at :root, which we then
         confirm via a chained rule below. */
    }
    /* Root-driven duck sizing: only applies when --dddk-indicator-duck-url
       is defined (any non-none value). Uses --dddk-indicator-duck-size
       so hosts can tune. */
    [${UI_ATTR}="indicator-dots"][data-duck] span {
      width: var(--dddk-indicator-duck-size, 16px);
      height: var(--dddk-indicator-duck-size, 16px);
      background-color: transparent;
    }
    [${UI_ATTR}="indicator-dots"] span:nth-child(2) { animation-delay: 0.15s; }
    [${UI_ATTR}="indicator-dots"] span:nth-child(3) { animation-delay: 0.3s; }
    /* ── Thinking indicator: swim relay ─────────────────────────────
       Three side-view rubber-duck sprites bounce in relay-swim
       sequence + one larger "hero" sprite pulses. Reads as "the duck
       is paddling hard to think" — not 3 face-heads bouncing (that
       read as weird). Sprites default to the side-view swim pose
       (--dddk-swim-url), so the swim spritesheet is reused. */
    [${UI_ATTR}="indicator-swim"] {
      display: inline-flex;
      align-items: flex-end;
      gap: 4px;
      padding: 2px 0;
    }
    [${UI_ATTR}="indicator-swim-hero"] {
      width: 28px;
      height: 22px;
      background-image: var(--dddk-indicator-swim-url, none);
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center;
      animation: dddk-indicator-swim-hero var(--dddk-indicator-swim-duration, 1.5s) ease-in-out infinite;
    }
    [${UI_ATTR}="indicator-swim-pip"] {
      width: 14px;
      height: 12px;
      background-image: var(--dddk-indicator-swim-url, none);
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center;
      animation: dddk-indicator-swim-pip var(--dddk-indicator-swim-pip-duration, 1s) ease-in-out infinite;
    }
    [${UI_ATTR}="indicator-swim-pip"]:nth-of-type(2) { animation-delay: 0.15s; }
    [${UI_ATTR}="indicator-swim-pip"]:nth-of-type(3) { animation-delay: 0.3s; }
    [${UI_ATTR}="indicator-swim-pip"]:nth-of-type(4) { animation-delay: 0.45s; }
    @keyframes dddk-indicator-swim-hero {
      0%, 100% { transform: translateY(0)   scale(1); }
      50%      { transform: translateY(-4px) scale(1.08); }
    }
    @keyframes dddk-indicator-swim-pip {
      0%, 60%, 100% { transform: translateY(0);    opacity: 0.55; }
      30%           { transform: translateY(-6px); opacity: 1; }
    }

    /* ── Listening indicator: waveform bars ─────────────────────────
       Five vertical bars pulse independently, mimicking an audio
       waveform / mic level meter. No duck heads on this one (voice
       is a distinct interaction — three duck heads bouncing read as
       weird for "I'm listening"). Yellow brand colour matches the
       FAB's listening sonar rings. */
    [${UI_ATTR}="indicator-waves"] {
      display: inline-flex;
      align-items: center;
      gap: 3px;
      height: 22px;
    }
    [${UI_ATTR}="indicator-waves"] span {
      display: inline-block;
      width: 3px;
      height: 8px;
      background: var(--dddk-indicator-wave-color, #ffd93d);
      border-radius: 3px;
      transform-origin: center;
      animation: dddk-indicator-wave var(--dddk-indicator-wave-duration, 0.9s) ease-in-out infinite;
    }
    [${UI_ATTR}="indicator-waves"] span:nth-child(1) { animation-delay: 0s;    }
    [${UI_ATTR}="indicator-waves"] span:nth-child(2) { animation-delay: 0.1s;  }
    [${UI_ATTR}="indicator-waves"] span:nth-child(3) { animation-delay: 0.2s;  }
    [${UI_ATTR}="indicator-waves"] span:nth-child(4) { animation-delay: 0.15s; }
    [${UI_ATTR}="indicator-waves"] span:nth-child(5) { animation-delay: 0.05s; }
    @keyframes dddk-indicator-wave {
      0%, 100% { transform: scaleY(0.35); }
      50%      { transform: scaleY(1.6); }
    }

    [${UI_ATTR}="indicator-label"] {
      letter-spacing: 0.02em;
      position: relative;
    }
    /* Shimmer sweep — a soft light bar travels left-to-right under
       the indicator label, giving a "cooking, don't worry" feel.
       Purely decorative; kept subtle so it doesn't upstage the label. */
    [${UI_ATTR}="indicator"][data-state="processing"] [${UI_ATTR}="indicator-label"]::after {
      content: '';
      position: absolute;
      left: 0; right: 0; bottom: -3px;
      height: 2px;
      background: linear-gradient(
        90deg,
        transparent 0%,
        rgba(255,220,80,0.9) 35%,
        rgba(255,220,80,0.9) 55%,
        transparent 100%
      );
      background-size: 220% 100%;
      border-radius: 2px;
      animation: dddk-indicator-shimmer 1.6s linear infinite;
      opacity: 0.75;
    }
    @keyframes dddk-indicator-shimmer {
      from { background-position: 100% 0; }
      to   { background-position: -120% 0; }
    }
    @keyframes dddk-dot-bounce {
      0%, 80%, 100% { transform: scale(0.7); opacity: 0.55; }
      40% { transform: scale(1); opacity: 1; }
    }
    @keyframes dddk-indicator-pulse {
      0%, 100% {
        box-shadow:
          var(--dddk-shadow-md, 0 6px 20px rgba(0,0,0,0.25)),
          0 0 0 0 rgba(99, 102, 241, 0.55);
      }
      50% {
        box-shadow:
          var(--dddk-shadow-md, 0 6px 20px rgba(0,0,0,0.25)),
          0 0 0 12px rgba(99, 102, 241, 0);
      }
    }
  `);
}
